import { useDisclosure } from "@mantine/hooks";
import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useUnit } from "effector-react";
import { Controller, useForm } from "react-hook-form";
import { UnifiedDialog } from "../../shared/ui";
import { createCampaignModel } from "./model";
import { useEffect } from "react";
import { CsvFileInput } from "../process-article/csv-file-input";
import { IconTrash } from "@tabler/icons-react";
import { CreateCampaignPayload } from "../../entities/campaigns/types";

type FormValues = CreateCampaignPayload;

export const CreateCampaign = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [isPending] = useUnit([createCampaignModel.createFx.pending]);

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      category: "",
      articles: [],
    },
  });

  const onSubmit = (data: FormValues) => {
    createCampaignModel.createFx(data).then(() => {
      close();
    });
  };

  const handleClose = () => {
    close();
  };

  const articles = form.watch("articles");

  const handleRemoveTask = (url: string) => {
    const newTasks = articles.filter((task) => task.url !== url);
    form.setValue("articles", newTasks);
  };

  const handleRemoveAllTasks = () => {
    form.setValue("articles", []);
  };

  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  return (
    <>
      <UnifiedDialog
        opened={opened}
        onClose={handleClose}
        onOk={form.handleSubmit(onSubmit)}
        loading={isPending}
        okText="Submit"
        title="Add a campaign"
        body={
          <Stack>
            <Controller
              control={form.control}
              name="name"
              rules={{
                required: "Field is required",
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Enter campaign name"
                  placeholder="e.g Forbes LATAM"
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={form.control}
              name="category"
              rules={{
                required: "Field is required",
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Enter campaign category"
                  placeholder="e.g Finance"
                  error={fieldState.error?.message}
                />
              )}
            />
            {articles?.length === 0 ? (
              <CsvFileInput
                onUpdate={(articles) => form.setValue("articles", articles)}
              />
            ) : (
              <Stack gap={"sm"}>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Tasks ({articles.length})
                  </Text>
                  <Text
                    size="sm"
                    fw={500}
                    onClick={handleRemoveAllTasks}
                    style={{ cursor: "pointer" }}
                  >
                    Remove all
                  </Text>
                </Group>
                <Stack gap={"xs"}>
                  {articles.map((article, index) => (
                    <Group
                      key={article.url}
                      wrap="nowrap"
                      justify="space-between"
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "10px",
                        borderRadius: "5px",
                      }}
                    >
                      <Text truncate size="sm">
                        {index + 1}. {article.url}
                      </Text>

                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        color="red"
                        mr={0}
                        w={30}
                        styles={{
                          icon: {
                            justifyContent: "right",
                          },
                        }}
                        onClick={() => handleRemoveTask(article.url)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        }
      />

      <Button bg="green" size="md" onClick={open}>
        Add a campaign{" "}
      </Button>
    </>
  );
};
